import { last } from 'ramda';
import { useEffect } from 'react';
import { Platform } from 'react-native';
// @ts-ignore
import KeyboardManager from 'react-native-keyboard-manager';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { useBehaviorSubjectUpdater } from '../functions/hooks/useBehaviorSubject';

const isDebugEnabled = false;

export const kDefaultKeyboardDistanceFromTextField = 30;

const keyboardAlteringStack$ = new BehaviorSubject<
  { key: string; on: boolean }[]
>([]);

const enableAutomaticKeyboard = () => {
  KeyboardManager.setKeyboardDistanceFromTextField(
    kDefaultKeyboardDistanceFromTextField,
  );
  KeyboardManager.setEnable(true);
  KeyboardManager.setEnableAutoToolbar(true);
  isDebugEnabled && console.log('enable IQ KeyboardManager');
};

const disableAutomaticKeyboard = () => {
  KeyboardManager.setEnable(false);
  KeyboardManager.setEnableAutoToolbar(false);
  isDebugEnabled && console.log('disabled IQ KeyboardManager');
};

export const useSetIQKeyboardManager = (
  enabled: boolean,
  keyboardDistanceFromTextField?: number,
) => {
  const setAlteringStack = useBehaviorSubjectUpdater(keyboardAlteringStack$);

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      return;
    }
    const key = Math.random().toString();
    setAlteringStack(p => [...p, { key, on: enabled }]);

    // set the distance between keyboard and text field when necessary
    if (keyboardDistanceFromTextField != null) {
      KeyboardManager.setKeyboardDistanceFromTextField(
        keyboardDistanceFromTextField,
      );
    }
    return () => {
      setAlteringStack(p => p.filter(x => x.key !== key));
      if (keyboardDistanceFromTextField != null) {
        // restore the default distance between keyboard and text field
        KeyboardManager.setKeyboardDistanceFromTextField(
          kDefaultKeyboardDistanceFromTextField,
        );
      }
    };
  }, [enabled, keyboardDistanceFromTextField, setAlteringStack]);
};

export const useKeyboardManagerMonitor = () => {
  useEffect(() => {
    if (Platform.OS !== 'ios') {
      return;
    }
    const sub = keyboardAlteringStack$
      .pipe(
        map(stacks => last(stacks)?.on ?? false),
        startWith(false),
        distinctUntilChanged(),
      )
      .subscribe(enabled => {
        if (enabled) {
          enableAutomaticKeyboard();
        } else {
          disableAutomaticKeyboard();
        }
      });
    return () => {
      sub.unsubscribe();
    };
  }, []);
};
